/**
 * Canvas MiniMap — GPU 加速的小地图渲染器
 *
 * 替代 ReactFlow 原生 SVG MiniMap，解决拖动节点时的闪烁问题：
 * - 原生 MiniMap 用 SVG <rect> per node，CPU 光栅化，拖动时 selector$1 每帧重算 boundingRect → 闪烁
 * - Canvas MiniMap 用单个 Canvas，rAF 驱动，与 React 渲染周期解耦
 * - Canvas 2D 在 GPU 合成层上运行，批量绘制所有节点矩形
 *
 * 交互：
 * - pannable：拖动 MiniMap 移动视口
 * - zoomable：滚轮缩放
 */
import React, { useEffect, useRef, useCallback, memo } from 'react';
import { useStoreApi, useReactFlow } from '@xyflow/react';
import { NodeType } from '../../../types';
import { NODE_TYPE_CONFIG } from '../../../config/nodeTypeConfig';

interface CanvasMiniMapProps {
  width: number;
  height: number;
  maskColor?: string;
  bgColor?: string;
  pannable?: boolean;
  zoomable?: boolean;
  /** 是否可见（用于暂停 rAF 节省 CPU） */
  visible?: boolean;
}

const OFFSET_SCALE = 5;
const ZOOM_STEP = 1;
// 缩放范围限制（与 FlowEditor 的 minZoom/maxZoom 一致）
const MIN_ZOOM = 0.1;
const MAX_ZOOM = 3;

// 节点颜色映射（与原 MiniMapToolbar nodeColor 一致）
const getNodeColor = (node: { type?: string }): string => {
  const config = NODE_TYPE_CONFIG[node.type as NodeType];
  return config?.color?.includes('cyan') ? '#22d3ee' :
         config?.color?.includes('purple') ? '#a855f7' :
         config?.color?.includes('emerald') ? '#10b981' :
         config?.color?.includes('orange') ? '#f97316' :
         '#64748b';
};

const CanvasMiniMapComponent: React.FC<CanvasMiniMapProps> = ({
  width: elementWidth,
  height: elementHeight,
  maskColor = 'rgba(0, 0, 0, 0.8)',
  bgColor = '#27272a', // zinc-800
  pannable = true,
  zoomable = true,
  visible = true,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const store = useStoreApi();
  const { setViewport } = useReactFlow();

  // viewScale 在 draw 中更新，pannable 交互中读取
  const viewScaleRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) return;

    // 不可见时暂停 rAF，避免无谓的 CPU 占用
    if (!visible) return;

    let rafId = 0;
    let running = true;

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      const w = Math.floor(elementWidth * dpr);
      const h = Math.floor(elementHeight * dpr);
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
        canvas.style.width = `${elementWidth}px`;
        canvas.style.height = `${elementHeight}px`;
      }
    };

    resize();

    const draw = () => {
      if (!running) return;

      const dpr = window.devicePixelRatio || 1;
      const state = store.getState();
      const { nodeLookup, transform, width: flowWidth, height: flowHeight } = state;
      const [tx, ty, tz] = transform;

      // 计算 viewBB（当前视口在内部坐标系中的位置）
      const viewBB = {
        x: -tx / tz,
        y: -ty / tz,
        width: flowWidth / tz,
        height: flowHeight / tz,
      };

      // 计算 boundingRect（所有可见节点的边界框）
      let boxX = Infinity, boxY = Infinity, boxX2 = -Infinity, boxY2 = -Infinity;
      let hasVisibleNodes = false;
      nodeLookup.forEach((node) => {
        if (node.hidden) return;
        const { x, y } = node.internals.positionAbsolute;
        const w = node.measured?.width ?? node.width ?? 0;
        const h = node.measured?.height ?? node.height ?? 0;
        if (x < boxX) boxX = x;
        if (y < boxY) boxY = y;
        if (x + w > boxX2) boxX2 = x + w;
        if (y + h > boxY2) boxY2 = y + h;
        hasVisibleNodes = true;
      });

      const boundingRect = hasVisibleNodes
        ? { x: boxX, y: boxY, width: boxX2 - boxX, height: boxY2 - boxY }
        : viewBB;

      // 计算 viewBox（与 ReactFlow MiniMap 一致）
      const scaledWidth = boundingRect.width / elementWidth;
      const scaledHeight = boundingRect.height / elementHeight;
      const viewScale = Math.max(scaledWidth, scaledHeight);
      const viewWidth = viewScale * elementWidth;
      const viewHeight = viewScale * elementHeight;
      const offset = OFFSET_SCALE * viewScale;
      const vbX = boundingRect.x - (viewWidth - boundingRect.width) / 2 - offset;
      const vbY = boundingRect.y - (viewHeight - boundingRect.height) / 2 - offset;
      const vbW = viewWidth + offset * 2;
      const vbH = viewHeight + offset * 2;

      viewScaleRef.current = viewScale;

      // 清除并填充背景（设备像素坐标）
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.fillStyle = bgColor;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // 应用 viewBox → Canvas 像素变换
      const scaleX = elementWidth / vbW;
      const scaleY = elementHeight / vbH;
      ctx.setTransform(scaleX * dpr, 0, 0, scaleY * dpr, -vbX * scaleX * dpr, -vbY * scaleY * dpr);

      // 绘制节点矩形
      nodeLookup.forEach((node) => {
        if (node.hidden) return;
        const { x, y } = node.internals.positionAbsolute;
        const w = node.measured?.width ?? node.width ?? 0;
        const h = node.measured?.height ?? node.height ?? 0;
        ctx.fillStyle = getNodeColor(node.internals.userNode);
        ctx.fillRect(x, y, w, h);
      });

      // 绘制 mask（even-odd 填充：外框 + viewport 矩形）
      ctx.fillStyle = maskColor;
      ctx.beginPath();
      ctx.rect(vbX, vbY, vbW, vbH); // 外框
      ctx.rect(viewBB.x, viewBB.y, viewBB.width, viewBB.height); // viewport 矩形（透明）
      ctx.fill('evenodd');

      // 绘制 viewport 边框
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
      ctx.lineWidth = 1 / viewScale; // 保持 1px 宽度
      ctx.strokeRect(viewBB.x, viewBB.y, viewBB.width, viewBB.height);

      rafId = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      running = false;
      cancelAnimationFrame(rafId);
    };
  }, [store, elementWidth, elementHeight, maskColor, bgColor, visible]);

  // pannable 交互
  const handleMouseDown = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!pannable) return;
    e.preventDefault();
    const startClientX = e.clientX;
    const startClientY = e.clientY;
    const transform = store.getState().transform;

    const handleMouseMove = (ev: MouseEvent) => {
      const dx = ev.clientX - startClientX;
      const dy = ev.clientY - startClientY;
      const viewScale = viewScaleRef.current;
      // 与 ReactFlow XYMinimap 一致的 moveScale 计算
      const moveScale = viewScale * Math.max(transform[2], Math.log(transform[2]));
      setViewport({
        x: transform[0] - dx * moveScale,
        y: transform[1] - dy * moveScale,
        zoom: transform[2],
      });
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [pannable, store, setViewport]);

  // zoomable 交互：原生非 passive 监听（React onWheel 默认 passive，preventDefault
  // 无效并告警 "Unable to preventDefault inside passive event listener"，且页面会跟随滚动）
  const handleWheel = useCallback((e: WheelEvent) => {
    if (!zoomable) return;
    e.preventDefault();
    const transform = store.getState().transform;
    const factor = e.ctrlKey ? 10 : 1;
    const pinchDelta = -e.deltaY *
      (e.deltaMode === 1 ? 0.05 : e.deltaMode ? 1 : 0.002) *
      ZOOM_STEP;
    const nextZoom = Math.min(Math.max(transform[2] * Math.pow(2, pinchDelta * factor), MIN_ZOOM), MAX_ZOOM);
    setViewport({
      x: transform[0],
      y: transform[1],
      zoom: nextZoom,
    });
  }, [zoomable, store, setViewport]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !zoomable) return;
    canvas.addEventListener('wheel', handleWheel, { passive: false });
    return () => canvas.removeEventListener('wheel', handleWheel);
  }, [handleWheel, zoomable]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        width: elementWidth,
        height: elementHeight,
        cursor: pannable ? 'grab' : 'default',
      }}
      onMouseDown={handleMouseDown}
    />
  );
};

// memo + 自定义比较：props 全是基本类型，浅比较即可
// 防止 MiniMapToolbar 因 zoomPercent/showMiniMap/snapToGrid 变化重渲染时牵连 CanvasMiniMap
export const CanvasMiniMap = memo(CanvasMiniMapComponent);
